/* eslint-disable jsx-a11y/label-has-associated-control */
import { FieldConfig, useField, useFormikContext } from 'formik';
import React, { useEffect, useRef, useState } from 'react';
import { InputActionMeta } from 'react-select';
import { toast } from 'react-toastify';
import SearchSelect from './SearchSelect';

interface IProps extends FieldConfig {
  label: string;
  placeholder: string;
  options: any;
  onBlur?: (e?: React.ChangeEvent<HTMLInputElement>) => void;
  onInput?: (e: any) => void;
  onFocus?: () => void;
  showBlue?: boolean;
  showGreen?: boolean;
  stepName?: string;
  shouldAutoUpdate?: boolean;
  updateCallback?: (...args: any) => Promise<any> | any;
  refreshCallback?: (...args: any) => Promise<any> | any;
  getOptionLabel?: (...args: any) => Promise<any> | any;
  onInputChange?: (newValue: string, actionMeta: InputActionMeta) => void;
  children?: any;
  disabled?: boolean;
  isMulti?: boolean; // Add the isMulti prop here
  isUpdating?: boolean;
}

export function FormikSearchSelect<T>({
  label,
  placeholder,
  shouldAutoUpdate,
  options,
  showBlue,
  showGreen,
  stepName,
  updateCallback,
  refreshCallback,
  getOptionLabel,
  onFocus,
  onInput,
  onInputChange,
  disabled,
  isMulti = false,
  ...props
}: IProps): any {
  const [field, meta, helpers] = useField(props);
  const { handleBlur } = useFormikContext<T>();
  const { setValue, setTouched } = helpers;
  const [colorBlueState, setColorBlueState] = useState({});
  const [colorGreenState, setColorGreenState] = useState({});
  const [colorRedState, setColorRedState] = useState({});
  const [isUpdating, setIsUpdating] = useState(false);
  const [isBlured, setIsBlured] = useState(true);

  const setFieldProps = (selectedOption: any) => {
    if (isMulti) {
      // Ensure selectedOption is an array before mapping
      if (Array.isArray(selectedOption)) {
        setValue(selectedOption.map((opt: any) => opt.value));
      } else {
        setValue([]); // Set empty array if nothing is selected
      }
    } else {
      // Handle the single selection case
      setValue(selectedOption?.value || null);
    }
  };

  const defaultValue = (choices: any, value: any) => {
    if (!Array.isArray(choices)) {
      return isMulti ? [] : ''; // Return empty array for multi-select or empty string for single-select
    }

    if (isMulti) {
      // Ensure value is an array before calling includes
      return Array.isArray(value)
        ? choices.filter((option: any) => value.includes(option.value))
        : [];
    }

    // Handle single select case
    return choices.find((option: any) => option.value === value) || '';
  };

  const { initialValue, value, error } = meta;
  const { name } = field;

  useEffect(() => {
    if (value !== initialValue) {
      setColorBlueState({ [name]: name });
    } else {
      setColorBlueState({});
    }
    return () => {
      setColorGreenState({});
    };
  }, [value]);

  const processOnBlur = async () => {
    if (shouldAutoUpdate) {
      setColorRedState({});
      let updateObject;
      if (!error) {
        const oldVal: string = initialValue;
        let newVal: string | null = value;
        if (newVal !== oldVal) {
          if (newVal === '') {
            newVal = null;
          }
          updateObject = { [name]: newVal };
          try {
            setIsUpdating(true);
            if (updateCallback) await updateCallback(updateObject);
            setIsUpdating(false);
            setColorGreenState({ [name]: true });
            if (refreshCallback) await refreshCallback(updateObject);
            setColorBlueState({});
            setTimeout(() => {
              setColorBlueState({});
              setColorGreenState({});
              setColorRedState({});
            }, 2000);
          } catch (err: any) {
            setColorRedState({ [name]: true });
            toast.error('An error occured', err);
          }
        }
      }
    }
  };

  const selectReference = useRef(name);

  useEffect(() => {
    if (meta.touched && meta.error) {
      (selectReference.current as unknown as HTMLSelectElement).focus();
    }
  }, [meta.touched, meta.error]);
  const showError = isBlured && meta.touched && meta.error;

  return (
    <div className="relative" style={{ borderColor: 'blue' }}>
      <div className="text-xs h-4 px-2 text-red-00 ">
        {showError ? meta.error : ''}
      </div>
      <SearchSelect
        id={label}
        onChange={(selectedOption: any) => {
          setFieldProps(selectedOption);
          if (onInput) onInput(selectedOption);
        }}
        label={label}
        placeholder={placeholder}
        options={options}
        value={field.value ? defaultValue(options, field.value) : ''}
        onBlur={(e: any) => {
          handleBlur(e);
          processOnBlur();
          setIsBlured(true);
        }}
        onFocus={() => {
          if (onFocus) onFocus();
          setIsBlured(false);
        }}
        onMenuOpen={() => setTouched(true)}
        showBlue={name in colorBlueState}
        showGreen={name in colorGreenState}
        showRed={name in colorRedState}
        isUpdating={props.isUpdating || isUpdating}
        selectRef={selectReference}
        isDisabled={disabled}
        isMulti={isMulti}
      />
    </div>
  );
}

FormikSearchSelect.defaultProps = {
  onFocus: () => ({}),
  onBlur: () => ({}),
  getOptionLabel: () => ({}),
  showBlue: false,
  showGreen: false,
  shouldAutoUpdate: false,
  stepName: '',
  refreshCallback: () => ({}),
  updateCallback: () => ({}),
  onInputChange: () => ({}),
  onInput: () => ({}),
  disabled: false,
  children: <></>,
};

export default FormikSearchSelect;
